/**
 * PATH: packages/email-hub/src/feature/components/RowAttributionChips.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENTS-ROWATTRIBUTIONCHIPS-001
 * PURPOSE: The two per-row header chips the Inbox stamps next to the
 *          timestamp — the mailbox-attribution pill (whose shared
 *          mailbox a row landed in) and the dedup relation pill
 *          ("Reminder / Follow-up of …"). Extracted verbatim from the
 *          InboxPage row map so EVERY surface (Inbox + Sent / Archived /
 *          Starred / Binned via FolderEmailRow) renders them from ONE
 *          source — no per-folder fork, no drift.
 * WHY:     Operator 2026-06-17: "big difference in layout and
 *          functionality gap" between the Inbox and the other folders.
 *          The Inbox computed these chips inline; the folders had none.
 *          One shared builder closes the gap and keeps the two callers
 *          in lockstep.
 * FLOW:
 *   ┌───────┐   ┌──────────────────────────────┐   ┌────────────┐
 *   │ Email │ → │ mailbox dedup / relation_kind │ → │ pill / null │
 *   └───────┘   └──────────────────────────────┘   └────────────┘
 * DEPENDENCIES:
 *   - ./MailboxChip: the presentational mailbox pill + dot-stack group.
 * RELATED:
 *   - pages/InboxPage.tsx: original (now consuming) caller.
 *   - components/FolderEmailRow.tsx: folder-surface caller.
 */
import type { ReactNode } from 'react'
import { BellRing, Clock, Inbox, RefreshCw } from 'lucide-react'

import type { Email } from '../hooks/useEmails'
import { formatResurfaceTime } from '../lib/resurfaceTimes'
import { MailboxChip, MailboxChipGroup } from './MailboxChip'

/** The Inbox enriches each row with a deduped `mailboxes` array (which
 *  shared mailbox copies a conversation landed in) — a field the base
 *  `Email` type doesn't carry (folder lists don't dedup). Accept it as
 *  optional so BOTH callers (Inbox deduped rows + raw folder rows) type
 *  cleanly; absent → the legacy `synced_by_*` attribution path. */
type AttributableEmail = Email & {
  mailboxes?: Array<{ email: string; name?: string | null }>
}

/**
 * MailboxAttributionChip(email, viewer) → the "whose mailbox" pill.
 * Three render paths, identical to the Inbox:
 *   - 2+ deduped mailbox copies → <MailboxChipGroup> (dot stack).
 *   - exactly 1 mailbox         → <MailboxChip>. Suppressed when it's the
 *                                 viewer's own AND the org has a single
 *                                 connected mailbox (a solo inbox would
 *                                 otherwise stamp "you" on every row). When
 *                                 SEVERAL mailboxes are connected we render the
 *                                 own pill too (labelled by local-part), so the
 *                                 operator can tell a personal-mailbox email
 *                                 apart from a shared one (operator 2026-06-21:
 *                                 "there are multiple mailboxes here … how do I
 *                                 know which mailbox it is from").
 *   - legacy unstamped row      → "?" owner chip (same own-suppression rule).
 */
export function MailboxAttributionChip({
  email,
  currentUserEmail,
  currentUserId,
  neutral = false,
  showOwnMailbox = false,
}: {
  email: AttributableEmail
  currentUserEmail?: string | null
  currentUserId?: number | null
  /** De-saturate on workflow-framed rows so the pill stops competing
   *  with the board colour + AI tag. */
  neutral?: boolean
  /** When true (the org has 2+ connected mailboxes), do NOT suppress the
   *  viewer's OWN single-mailbox pill — render it labelled by local-part so
   *  personal vs shared is unambiguous. Defaults false (solo inbox: a bare
   *  "you" on every row is noise). */
  showOwnMailbox?: boolean
}): ReactNode {
  const mbs = email.mailboxes || []
  if (mbs.length > 1) {
    return (
      <MailboxChipGroup
        mailboxes={mbs}
        currentUserEmail={currentUserEmail}
        neutral={neutral}
      />
    )
  }
  if (mbs.length === 1) {
    const mb = mbs[0]
    const isMe =
      !!currentUserEmail &&
      mb.email.toLowerCase() === currentUserEmail.toLowerCase()
    if (isMe && !showOwnMailbox) return null
    return (
      <MailboxChip
        compact
        neutral={neutral}
        syncedByEmail={mb.email}
        syncedByName={mb.name}
        isCurrentUser={isMe}
      />
    )
  }
  const legacyIsMe =
    !!currentUserId && email.synced_by_user_id === currentUserId
  if (legacyIsMe && !showOwnMailbox) return null
  return (
    <MailboxChip
      compact
      neutral={neutral}
      syncedByEmail={email.synced_by_mailbox_email || email.synced_by_email}
      syncedByName={email.synced_by_mailbox_name || email.synced_by_name}
      isCurrentUser={legacyIsMe}
    />
  )
}

/**
 * ReceivingMailboxVia(email) → "via <full mailbox address>" line for the
 * READER headers (EmailReader + EmailPreviewPane).
 *
 * Why full address, not the chip's local-part: the row chips shorten to
 * "abhishek" — but an org with several connected mailboxes can have the
 * SAME local-part on different domains (operator 2026-07-09: "as it is to
 * Abhishek I can't tell which Abhishek … is it finsoeasy.com domain or
 * what?"). The reader has the horizontal room, so it names the mailbox
 * unambiguously. Renders null only when the row is entirely unstamped
 * (legacy pre-attribution rows).
 */
export function ReceivingMailboxVia({ email }: { email: AttributableEmail }): ReactNode {
  const mailboxAddress =
    email.synced_by_mailbox_email ||
    email.mailboxes?.[0]?.email ||
    email.synced_by_email
  if (!mailboxAddress) return null
  const extra = (email.mailboxes?.length ?? 0) - 1
  return (
    <span
      title={`This email landed in mailbox ${mailboxAddress}${extra > 0 ? ` (and ${extra} other connected mailbox${extra > 1 ? 'es' : ''})` : ''}`}
      data-testid="email-receiving-mailbox"
      className="inline-flex items-center gap-1 text-[12px] text-foreground"
    >
      <Inbox className="h-3.5 w-3.5 shrink-0 text-emerald-700 dark:text-emerald-400" aria-hidden />
      <span>
        via{' '}
        <span className="font-semibold text-emerald-800 dark:text-emerald-300">
          {mailboxAddress}
        </span>
        {extra > 0 ? ` +${extra}` : ''}
      </span>
    </span>
  )
}

/**
 * RelationChip(email) → a compact "Reminder / Follow-up of …" pill when
 * the backend (dedup Layer C) linked this row to an earlier email via
 * `relation_kind`. Renders null when the row has no relation.
 */
export function RelationChip({ email }: { email: Email }): ReactNode {
  const kind = email.relation_kind
  if (!kind) return null
  const labelMap: Record<string, string> = {
    reminder: 'Reminder',
    follow_up: 'Follow-up',
    resend: 'Resend',
    forward: 'Forward',
  }
  const label = labelMap[kind]
  if (!label) return null
  const ofSubject = email.related_to_subject
  return (
    <span
      title={ofSubject ? `${label} of "${ofSubject}"` : label}
      className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded-full font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
      data-testid="email-relation-chip"
    >
      <RefreshCw className="h-2.5 w-2.5 shrink-0" aria-hidden />
      <span className="truncate max-w-[90px]">{label}</span>
    </span>
  )
}

/**
 * ResurfaceChip(email) → the resurface-train state pill. Two states,
 * mutually exclusive (a snoozed row can't be awaiting a nudge visibly):
 *   - "Snoozed <time>"  — snoozed_until in the future (visible in search
 *     / snoozed filters; the default inbox hides these rows entirely).
 *   - "Awaiting reply"  — needs_reply set on the sent copy; auto-cleared
 *     server-side when a newer inbound message lands in the thread.
 * Renders null for the vast majority of rows that carry neither.
 */
export function ResurfaceChip({ email }: { email: Email }): ReactNode {
  const snoozedUntil = email.snoozed_until
  if (snoozedUntil && new Date(snoozedUntil).getTime() > Date.now()) {
    const when = formatResurfaceTime(snoozedUntil)
    return (
      <span
        title={`Snoozed — resurfaces ${when}`}
        className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded-full font-medium bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300"
        data-testid="email-snoozed-chip"
      >
        <Clock className="h-2.5 w-2.5 shrink-0" aria-hidden />
        <span className="truncate max-w-[110px]">Snoozed {when}</span>
      </span>
    )
  }
  if (email.needs_reply) {
    const due = email.followup_at
      ? ` — nudge ${formatResurfaceTime(email.followup_at)}`
      : ''
    return (
      <span
        title={`Awaiting a reply${due}`}
        className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded-full font-medium bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300"
        data-testid="email-needs-reply-chip"
      >
        <BellRing className="h-2.5 w-2.5 shrink-0" aria-hidden />
        <span className="truncate max-w-[110px]">Awaiting reply</span>
      </span>
    )
  }
  return null
}
